import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { css } from '@emotion/react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  type IButtonProps,
  Menu,
  Portal,
  Text,
  Tooltip,
} from '@lego/klik-ui';
import { GripDotsVertical, RedoSolid, RefreshBold, Settings } from '@lego/klik-ui/icons';
import type { ColumnOrderState, RowData } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import type { Column, VisibilityState } from './appTable';

type ControlProps<T extends RowData> = {
  columnOrder: ColumnOrderState;
  columnVisibility?: VisibilityState;
  columns: Column<T>[];
  isValidating?: boolean;
  refresh?: () => void;
  reset?: () => void;
  setColumnOrder: (columnOrder: ColumnOrderState) => void;
  setColumnVisibility?: (newColumnVisibility: VisibilityState) => void;
  addElement?: React.FC<IButtonProps>;
  toolbarAddon?: ReactNode;
};

const baseButtonProps: IButtonProps = {
  sx: {
    '&:first-of-type': {
      borderLeftRadius: '0.4em',
    },
    '&:last-child': {
      borderRightRadius: '0.4em',
    },
  },
  borderRadius: 'none',
  size: 'sm',
} as const;

const rotateStyle = css`
  @keyframes rotate {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(-360deg);
    }
  }
  animation: rotate 0.8s infinite ease-in-out;
`;

type SortableColumnItemProps<T extends RowData> = {
  column: Column<T>;
};

const SortableColumnItem = <T extends RowData>({ column }: SortableColumnItemProps<T>) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.id,
  });

  return (
    <Flex
      alignItems="center"
      gap={2}
      justifyContent="space-between"
      opacity={isDragging ? 0.5 : 1}
      p={2}
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <Box
        {...attributes}
        {...listeners}
        color="slate.400"
        cursor="grab"
        style={{ touchAction: 'none' }}
      >
        <GripDotsVertical />
      </Box>
      <Text flex={1}>{column.columnDef.header?.toString()}</Text>
      <Checkbox isChecked={column.getIsVisible()} onChange={column.getToggleVisibilityHandler()} />
    </Flex>
  );
};

export const TableControl = <T extends RowData>(props: ControlProps<T>) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const controlButtonProps: IButtonProps = {
    ...baseButtonProps,
    sx: {
      '&:first-of-type': { borderLeftRadius: props.addElement ? 0 : '0.4em' },
      '&:last-child': { borderRightRadius: '0.4em' },
    },
  };

  const orderedColumns: Column<T>[] =
    props.columnOrder.length > 0
      ? [
          ...props.columnOrder
            .map((id) => props.columns.find((c) => c.id === id))
            .filter((c): c is Column<T> => c !== undefined),
          ...props.columns.filter((c) => !props.columnOrder.includes(c.id)),
        ]
      : props.columns;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const ids = orderedColumns.map((c) => c.id);
    const oldIndex = ids.indexOf(active.id as string);
    const newIndex = ids.indexOf(over.id as string);
    props.setColumnOrder(arrayMove(ids, oldIndex, newIndex));
  };

  return (
    <Flex align="center" gap={props.toolbarAddon ? 2 : 0}>
      {props.toolbarAddon}
      <Flex>
        {props.addElement && <props.addElement {...baseButtonProps} />}
        {props.refresh && (
          <Tooltip label="Refresh data">
            <Button {...controlButtonProps} onClick={() => props.refresh?.()} position="relative">
              <Box
                css={props.isValidating && rotateStyle}
                height="100%"
                position="absolute"
                width="100%"
              >
                <RefreshBold
                  left="50%"
                  position="absolute"
                  style={{ transform: 'translate(-50%, -50%)' }}
                  top="50%"
                />
              </Box>
            </Button>
          </Tooltip>
        )}
        {props.setColumnVisibility && props.columnVisibility && (
          <Menu closeOnSelect={false}>
            <Menu.Button aria-label="Column settings" as={Button} {...controlButtonProps}>
              <Settings />
            </Menu.Button>

            <Portal>
              <Menu.List maxH="min(320px, calc(100dvh - 2rem))" minWidth="200px" overflowY="auto">
                <DndContext
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                  sensors={sensors}
                >
                  <SortableContext
                    items={orderedColumns.map((c) => c.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {orderedColumns.map((column) => (
                      <SortableColumnItem column={column} key={column.id} />
                    ))}
                  </SortableContext>
                </DndContext>
              </Menu.List>
            </Portal>
          </Menu>
        )}
        {props.reset && (
          <Tooltip label="Reset table: Clearing filters, sorts, hidden columns and column order">
            <Button {...controlButtonProps} onClick={props.reset}>
              <RedoSolid />
            </Button>
          </Tooltip>
        )}
      </Flex>
    </Flex>
  );
};
